import { Promise } from 'rsvp';
import { readOnly } from '@ember/object/computed';
import Service, { inject as service } from '@ember/service';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';

export default Service.extend({
  /**
   * Register services
   */
  companyCommandService : service('commands/company-commands'),
  session: service(),
  errorTracker: service('error-tracker'),
  state: service('states/routes/settings/company/payments'),
  store: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Add a new card for the company in the state using the given stripeToken
   *
   * @param {String} stripeToken
   * @return {Ember.RSVP.Promise}     Resolves with the new card on success.
   */
  addNewCard(stripeToken) {
    let company = this.get('state.company');
    if (!company) {
      this.get('errorTracker').error('addNewCard -- no company set in state.' + company.get('id'), this);
      return Promise.reject(false);
    }

    this.set('state.stripeTokenForNewCard', stripeToken);
    this.set('state.isAddingNewCard', true);

    return new Promise((resolve, reject) => {
      this.get('companyCommandService').addNewCardForCompany(company, stripeToken).then(card => {

        this.set('state.stripeTokenForNewCard', '');

        this.get('companyCommandService').makeCardDefault(card).then(defaultCard => {
          resolve(defaultCard);
        }).catch(e => {
          handleErrorResponseWithModal(
            this,
            e,
            'There was an error setting your new card as the default.',
            'addNewCard -- error setting default card.',
          );
          reject(e);
        }).finally(() => this.set('state.isAddingNewCard', false));
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error adding your card. Please try again.',
          'addNewCard -- error adding card.',
        );

        this.set('state.isAddingNewCard', false);

        reject(e);
      });
    });
  },

  /**
   * Delect a card for the company in our state.
   *
   * @param {Card} card
   * @return {Ember.RSVP.Promise}
   */
  deleteCard(card) {
    let company = this.get('state.company');
    if (!company) {
      this.get('errorTracker').error('deleteCard -- no company set in state.' + company.get('id'), this);
      return Promise.reject(false);
    }

    this.set('state.updatingCardId', card.get('id'));

    return new Promise((resolve, reject) => {
      let isDefault = card.get('isDefault');

      this.get('companyCommandService').deleteCardForCompany(company, card).then(() => {
        this.set('state.updatingCardId', '');

        if (isDefault) {
          let allCompanyCards = company.get('cards'),
            newDefaultCard = allCompanyCards.objectAt(0);

          this.makeCardDefault(newDefaultCard).then(() => {
            resolve(true);
          }).catch(e => {
            handleErrorResponseWithModal(
              this,
              e,
              'There was an error making your other card the default. Please try again.',
              'deleteCard -- error setting default card.',
            );
            reject(e);
          });

          return;
        }

        resolve(true);
      }).catch(e => {
        this.set('state.updatingCardId', '');

        handleErrorResponseWithModal(
          this,
          e,
          'There was an error deleting your card. Please try again.',
          'deleteCard -- error deleting card.',
        );
        reject(e);
      });
    });
  },

  /**
   * Make a card default for the user.
   *
   * @param {Card} card
   * @return {Ember.RSVP.Promise}
   */
  makeCardDefault(card) {
    let company = this.get('state.company');
    if (!company) {
      this.get('errorTracker').error('makeCardDefault -- no company set in state.' + company.get('id'), this);
      return Promise.reject(false);
    }

    this.set('state.updatingCardId', card.get('id'));

    return new Promise((resolve, reject) => {
      this.get('companyCommandService').makeCardDefault(card).then(() => {
        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(
          this,
          e,
          'There was an error making your card default. Please try again.',
          'makeCardDefault -- error defaulting card.',
        );

        reject(e);
      }).finally(() => this.set('state.updatingCardId', ''));
    });
  },

  /**
   * Manage a given company in this state.
   *
   * @param {String} companyId
   * @return {Ember.RSVP.Promise}   Resolve the company in success case.
   */
  manageCompany(companyId) {
    const company = this.store.peekRecord('company', companyId);
    if (company) {
      this.set('state.company', company);
      if (this.state.hasLoadedCards) {
        return Promise.resolve(company);
      }
    }

    return this.store.findRecord('company', companyId, {
      include: 'cards'
    }).then(company => {
      this.state.setProperties({
        company,
        hasLoadedCards: true,
      });
      return company;
    }).catch(error => {
      if (error.status === 403) {
        return null;
      }

      handleErrorResponseWithModal(
        this,
        error,
        'There was an error loading the company details. Please refresh and try again.',
        'manageCompany -- error loading company : ' + companyId,
      );
      throw error;
    });
  }
});
